import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { getDrawerUtilityClass } from './drawer-classes.esm.js';
import { DrawerContextProvider } from './drawer-context.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';
import { duration } from '../../../themes/theme-keys/transitions.esm.js';
import capitalize from '../../../utils/capitalize.esm.js';
import { composeClasses } from '../../../utils/classes.esm.js';
import { BoxPrivate } from '../../_primitives/box/box.esm.js';
import { FlexPrivate } from '../../_primitives/flex/flex.esm.js';
import Subject from '../../_utils/event-observer.esm.js';
import Modal from '../../_utils/modal/modal.esm.js';
import Slide from '../../_utils/slide/slide.esm.js';
import Pane from '../pane/pane.esm.js';

var _excluded = ["ownerState", "sx"],
  _excluded2 = ["anchor", "children", "className", "hideBackdrop", "onClose", "open", "PaneProps", "SlideProps", "TransitionComponent", "transitionDuration", "type"];
var useUtilityClasses = function useUtilityClasses(ownerState) {
  var classes = ownerState.classes,
    anchor = ownerState.anchor,
    type = ownerState.type;
  var slots = {
    root: ['root'],
    docked: [(type === 'permanent' || type === 'persistent') && 'docked'],
    modal: ['modal'],
    paper: ['paper', "paperAnchor".concat(capitalize(anchor)), type !== 'temporary' && "paperAnchorDocked".concat(capitalize(anchor))]
  };
  return composeClasses(slots, getDrawerUtilityClass, classes);
};
var oppositeDirection = {
  left: 'right',
  right: 'left',
  top: 'down',
  bottom: 'up'
};
function isHorizontal(anchor) {
  return ['left', 'right'].indexOf(anchor) !== -1;
}
function getAnchor(theme, anchor) {
  return theme.direction !== 'rtl' && isHorizontal(anchor) ? oppositeDirection[anchor] : anchor === 'top' ? 'down' : anchor === 'bottom' ? 'up' : anchor; // fall back direction
}
var defaultTransitionDuration = {
  enter: duration.enteringScreen,
  exit: duration.leavingScreen
};
var DrawerRoot = /*#__PURE__*/React.forwardRef(function DrawerRoot(props, ref) {
  return /*#__PURE__*/React.createElement(Modal, _extends({
    ref: ref,
    zIndex: "drawer"
  }, props));
});
var DrawerDockedRoot = /*#__PURE__*/React.forwardRef(function DrawerDockedRoot(props, ref) {
  return /*#__PURE__*/React.createElement(BoxPrivate, _extends({
    ref: ref
  }, props));
});
var DrawerPaper = /*#__PURE__*/React.forwardRef(function DrawerPaper(props, ref) {
  var ownerState = props.ownerState,
    sx = props.sx,
    rest = _objectWithoutProperties(props, _excluded);
  var style = useMemo(function () {
    return _objectSpread2(_objectSpread2({
      overflowY: 'auto',
      height: '100%',
      zIndex: 'drawer',
      // Add iOS momentum scrolling for iOS < 13.0
      WebkitOverflowScrolling: 'touch',
      // We disable the focus ring for mouse, touch and keyboard users.
      // At some point, it would be better to keep it for keyboard users.
      // :focus-ring CSS pseudo-class will help.
      outline: 0
    }, ownerState && ownerState.type === 'temporary' ? _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
      position: 'fixed',
      top: 0
    }, ownerState && ownerState.anchor === 'left' ? {
      left: 0
    } : {}), ownerState && ownerState.anchor === 'top' ? {
      top: 0,
      left: 0,
      right: 0,
      height: 'auto',
      maxHeight: '100%'
    } : {}), ownerState && ownerState.anchor === 'right' ? {
      right: 0
    } : {}), ownerState && ownerState.anchor === 'bottom' ? {
      top: 'auto',
      left: 0,
      bottom: 0,
      right: 0,
      height: 'auto',
      maxHeight: '100%'
    } : {}) : {}), sx);
  }, [ownerState, sx]);
  return /*#__PURE__*/React.createElement(Pane, _extends({
    as: FlexPrivate,
    ref: ref
  }, rest, {
    sx: style
  }));
});
var EMPTY_OBJECT = {};

/**
 * The props of the [Modal](/api/modal/) component are available
 * when `variant="temporary"` is set.
 */
var Drawer = /*#__PURE__*/React.forwardRef(function Drawer(props, ref) {
  var _props$anchor = props.anchor,
    anchorProp = _props$anchor === void 0 ? 'left' : _props$anchor,
    children = props.children,
    className = props.className,
    _props$hideBackdrop = props.hideBackdrop,
    hideBackdrop = _props$hideBackdrop === void 0 ? false : _props$hideBackdrop,
    onClose = props.onClose,
    _props$open = props.open,
    open = _props$open === void 0 ? false : _props$open,
    _props$PaneProps = props.PaneProps,
    PaneProps = _props$PaneProps === void 0 ? EMPTY_OBJECT : _props$PaneProps,
    SlideProps = props.SlideProps,
    _props$TransitionComp = props.TransitionComponent,
    TransitionComponent = _props$TransitionComp === void 0 ? Slide : _props$TransitionComp,
    _props$transitionDura = props.transitionDuration,
    transitionDuration = _props$transitionDura === void 0 ? defaultTransitionDuration : _props$transitionDura,
    _props$type = props.type,
    type = _props$type === void 0 ? 'temporary' : _props$type,
    other = _objectWithoutProperties(props, _excluded2);
  var theme = useSafeThemeUI(props);

  // Let's assume that the Drawer will always be rendered on user space.
  // We use this state is order to skip the appear transition during the
  // initial mount of the component.
  var mounted = useRef(false);
  useEffect(function () {
    mounted.current = true;
  }, []);
  var anchorInvariant = getAnchor(theme, anchorProp);
  var anchor = anchorProp;
  var context = useMemo(function () {
    var onEnteredObserver = new Subject('drawer-on-entered');
    var onExitedObserver = new Subject('drawer-on-exited');
    return {
      onEnteredObserver: onEnteredObserver,
      onExitedObserver: onExitedObserver
    };
  }, []);
  var handelOnEntered = useCallback(function () {
    var onEnteredObserver = context.onEnteredObserver;
    onEnteredObserver === null || onEnteredObserver === void 0 ? void 0 : onEnteredObserver.notify();
  }, [context]);
  var handelOnExited = useCallback(function () {
    var onExitedObserver = context.onExitedObserver;
    onExitedObserver === null || onExitedObserver === void 0 ? void 0 : onExitedObserver.notify();
  }, [context]);
  var ownerState = useMemo(function () {
    return {
      anchor: anchor,
      open: open,
      type: type
    };
  }, [anchor, open, type]);
  var classes = useUtilityClasses(ownerState);
  var drawer = /*#__PURE__*/React.createElement(DrawerPaper, _extends({
    square: true
  }, PaneProps, {
    className: classNames(classes.paper, PaneProps.className),
    ownerState: ownerState
  }), children);
  if (type === 'permanent') {
    return /*#__PURE__*/React.createElement(DrawerDockedRoot, _extends({
      className: classNames(classes.root, classes.docked, className)
      // ownerState={ownerState}
      ,
      ref: ref
    }, other), drawer);
  }
  var slidingDrawer = /*#__PURE__*/React.createElement(TransitionComponent, _extends({
    "in": open,
    direction: anchorInvariant,
    timeout: transitionDuration,
    appear: mounted.current,
    onEntered: handelOnEntered,
    onExited: handelOnExited
  }, SlideProps), drawer);
  if (type === 'persistent') {
    return /*#__PURE__*/React.createElement(DrawerContextProvider, {
      value: context
    }, /*#__PURE__*/React.createElement(DrawerDockedRoot, _extends({
      className: classNames(classes.root, classes.docked, className),
      ownerState: ownerState,
      ref: ref
    }, other), slidingDrawer));
  }

  // variant === temporary
  return /*#__PURE__*/React.createElement(DrawerRoot, _extends({
    className: classNames(classes.root, classes.modal, className),
    open: open,
    ownerState: ownerState,
    onClose: onClose,
    hideBackdrop: hideBackdrop,
    ref: ref
  }, other), slidingDrawer);
});

export default Drawer;
export { getAnchor, isHorizontal };
//# sourceMappingURL=drawer.esm.js.map
